import * as THREE from 'three';
import { DEEP_CRYPT, DUNGEON, SIMA, SIMA_CHEST, STRATUM, WARDEN } from '@somnhold/content';
import { PartyFrames } from './party-frames.ts';
import { simToWorld, WORLD_PER_HEX } from '@somnhold/render';
import { deepChest, hexToWorld, worldToHex, simaChest, type BattleState, type Command, type DungeonPuzzle, type Hero, type Hex } from '@somnhold/sim';
import { t } from '../i18n.ts';
import { h, setFill, setHidden, setStyle, setText, viewH, viewW } from '../ui/dom.ts';
import { ICONS } from '../ui/icons.ts';

/** Stairs going down, for the deep crypt's choice. */
const STAIRS = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 4h4v5h4v5h4"/><path d="M9 4v16h12v-6"/><path d="M5 9v10M2.5 16.5L5 19l2.5-2.5"/></svg>';

/** A chevron pointing down; turned to point at a way off screen. */
/** A phone, upright or on its side: the HUD panels go compact and fold so the room shows. */
const small = (): boolean => viewW() <= 640 || viewH() <= 500;
/** How long a new puzzle's whole order stays open on a small screen before folding (12 ticks a second). */
const PUZZLE_FOLD_TICKS = 7 * 12;
const CHEVRON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 8l7 8 7-8"/></svg>';

/** Ticks as m:ss (12 ticks a second), rounded up so the clock reads 0:00 only when it is out. */
const clock = (ticks: number): string => {
  const sec = Math.ceil(Math.max(0, ticks) / 12);
  return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
};
/**
 * El Párpado 1: the titan's shake, said in a line (when it comes, or the dust is up and it's time to stand still); down
 * the neck, the folds' breath too (open and how long till they shut, or shut and how long till they open).
 */
export const neckLine = (s: BattleState): string => {
  const r = s.rules?.neck;
  const st = s.neck;
  if (!r) return '';
  const shake = st?.warned ? t(s.rules?.climb ? 'side.climb.dustNow' : 'side.neck.dustNow') : t('side.neck.next', { s: Math.max(0, Math.ceil(((st?.shake ?? r.shake.first) - s.waveT) / 12)) });
  if (!s.map.dungeon?.doors.length || !s.started) return shake;
  const cycle = r.open + r.shut;
  const phase = s.waveT % cycle;
  const open = st?.open ?? true;
  const left = Math.max(1, Math.ceil(((open ? r.open : cycle) - phase) / 12));
  return `${t(open ? 'side.neck.open' : 'side.neck.shut', { s: left })} · ${shake}`;
};

/**
 * El Párpado 2, the climb: the bell's ropes (whole ●, cut ○), how many are pulling it (heroes beside it, and Ulla's
 * boars), and how far up it is (what to do when the dust is up is in the shake's line, neckLine).
 */
export const climbLine = (s: BattleState): string => {
  const r = s.rules?.climb;
  const e = s.escort;
  if (!r || !e) return '';
  const bell = s.bots?.find((b) => b.id === e.cart);
  const whole = s.climb?.ropes ?? r.ropes;
  const pulling = bell ? s.heroes.filter((h) => h.downTicks === 0 && (h.x - bell.x) ** 2 + (h.y - bell.y) ** 2 <= r.pull * r.pull).length : 0;
  const up = Math.round((100 * Math.min(e.next, e.road.length)) / Math.max(1, e.road.length));
  const ropes = '●'.repeat(whole) + '○'.repeat(Math.max(0, r.ropes - whole));
  const who = r.boars ? t('side.climb.pullingBoars', { n: pulling }) : t('side.climb.pulling', { n: pulling });
  return `${ropes} · ${who} · ${t('side.climb.up', { p: up })}`;
};

/** The race's last stretch: the clock goes red. */
const RACE_LATE_TICKS = 30 * 12;

/** Where to go next between fights: the door into the next room, or the stairs and the way out. */
type Way = { at: Hex; label: string };

/** One side of «Down, or back?»: where to stand, what it brings, and how far the group is along. A tap walks you there. */
class ChoiceTile {
  readonly el: HTMLElement;
  private fill = h('i.dg-opt-fill');
  readonly gain = h('span.dg-opt-gain');
  readonly note = h('span.dg-opt-note');

  constructor(kind: 'down' | 'back', icon: string, go: () => void) {
    this.el = h(
      `button.dg-opt.${kind}`,
      { type: 'button', 'data-test': `dg-${kind}`, onclick: go },
      this.fill,
      h('span.dg-opt-ico', { html: icon }),
      h('span.dg-opt-txt', {}, h('strong', {}, t(`deep.${kind}Name`)), h('span.dg-opt-where', {}, t(`deep.${kind}Where`), ' · ', h('u', {}, t('deep.tapToGo'))), this.gain, this.note),
    );
  }

  set(progress: number): void {
    const k = Math.min(1, Math.max(0, progress));
    setFill(this.fill, (Math.round(k * 100)) / 100);
    this.el.classList.toggle('going', k > 0);
  }
}


/**
 * The crypt's HUD: the party (who is down and how close they are to getting up), where you are
 * (room and what is left in it), the Warden's bar with its two thresholds, and a word when the floor
 * lights up under you.
 */
export class DungeonHud {
  readonly el: HTMLElement;
  readonly party: PartyFrames;
  private where = h('div.chip.dg-where', { role: 'status', 'aria-live': 'polite' });
  private whereTitle = h('b');
  private whereSub = h('span');
  private boss = h('div.dg-boss');
  private bossFill = h('i');
  private bossPhase = h('span.dg-phase');
  private tags = h('div.dg-tags');
  private bossName = h('b.dg-boss-title');
  /** The deep crypt: what to do now (the puzzle, or the stairs and the way out), with its progress. */
  private goal = h('div.dg-goal', { role: 'status', 'aria-live': 'polite', 'data-test': 'dg-goal' });
  private goalTitle = h('b');
  private goalText = h('p');
  private goalBar = h('i');
  private goalBar2 = h('i');
  private goalBars = h('div.dg-goal-bars');
  private down = new ChoiceTile('down', STAIRS, () => this.goTo(this.s().dungeon?.deep?.stairs ?? this.s().dungeon?.sima?.stairs));
  private back = new ChoiceTile('back', ICONS.chest, () => this.goTo(this.s().dungeon?.deep?.exit ?? this.s().dungeon?.sima?.exit));
  /** The Chasm: what was already said this run (floors, hazards, guardians' rules). */
  private simaSeen = { rev: 0, said: new Set<string>(), act: '' };
  /** The archivist's reading, under his own bar: what is left of it to cut. */
  private readFill = h('i');
  private read = h('div.dg-read', { 'data-test': 'sima-read' }, h('span', {}, t('sima.readDo')), h('div.bar', {}, this.readFill));
  /** The stairs panel folded down to its two buttons (a tap on its corner). */
  private fold = h('button.dg-fold', { type: 'button', 'data-test': 'dg-fold', 'aria-label': t('deep.fold'), title: t('deep.fold'), onclick: () => this.setFolded(!this.goal.classList.contains('folded')) }, '–');
  private choice = h('div.dg-choice', {}, this.down.el, this.back.el);
  private seen = { floor: 0, solved: new Set<number>(), fails: new Map<number, number>(), stairs: false, puzzle: -1 };
  /** On a small screen, the tick at which a puzzle's panel folds down to its title and bar (0: none pending). */
  private foldAt = 0;
  private tagEls = new Map<string, HTMLElement>();
  private ways = h('div.dg-ways');
  private wayEls: HTMLElement[] = [];
  private warned = new Set<number>();
  private hinted = new Set<string>();
  /** The race (bet 8): the clock in the room chip, with Brann's boast marked on its bar. */
  private race = h('div.dg-race', { 'data-test': 'race-clock' });
  private raceTime = h('b.dg-race-t');
  private raceFill = h('i');
  private raceNote = h('span.dg-race-note');
  private raceSec = -1;
  /** El Párpado 1, down the neck: the folds' breath and the next shake. */
  private neckEl = h('div.dg-neck', { 'data-test': 'neck-line' });

  constructor(
    private s: () => BattleState,
    private me: string,
    private names: Record<string, string>,
    private toast: (msg: string, ms?: number) => void,
    private send: (c: Command) => void = () => undefined,
  ) {
    this.party = new PartyFrames(me, names);
    this.where.append(this.whereTitle, this.whereSub);
    this.boss.append(
      h('div.dg-boss-name', {}, h('span', { html: ICONS.skull }), this.bossName, this.bossPhase),
      h('div.dg-boss-bar', {}, this.bossFill, h('span.dg-tick', { style: `left:${WARDEN.phase2AtBp / 100}%` }), h('span.dg-tick', { style: `left:${WARDEN.phase3AtBp / 100}%` })),
    );
    setText(this.bossName, t('dungeon.warden'));
    this.goal.append(this.fold, this.goalTitle, this.goalText, this.goalBars, this.choice);
    // On a phone it starts folded: the two buttons, without covering the room.
    this.setFolded(small());
    this.boss.append(this.read);
    const race = this.s().rules?.race;
    if (race) {
      const brann = Math.min(100, (100 * race.brann) / Math.max(1, race.limit));
      this.race.append(
        h('div.dg-race-top', {}, h('span.dg-race-ico', { html: ICONS.hourglass }), this.raceTime),
        h('div.dg-race-bar', {}, this.raceFill, h('span.dg-race-brann', { style: `left:${brann}%`, title: t('bet.race.brannAt', { t: clock(race.brann) }) })),
        this.raceNote,
        h('span.dg-race-rule', {}, t('bet.race.hud')),
      );
      this.where.append(this.race);
      this.where.classList.add('racing');
    }
    if (this.s().rules?.neck) this.where.append(this.neckEl);
    this.el = h('div.dg-hud', {}, this.ways, this.party.el, this.where, this.goal, this.boss, this.tags);
  }

  /** The race's clock: what is left, how far along (Brann's time marked), red near the end. */
  private raceUpdate(s: BattleState): void {
    const r = s.rules?.race;
    if (!r) return;
    const spent = s.started ? Math.min(s.waveT, r.limit) : 0;
    const sec = Math.ceil(spent / 12);
    if (sec === this.raceSec && s.result === 'running') return;
    this.raceSec = sec;
    const left = r.limit - spent;
    setText(this.raceTime, clock(left));
    setFill(this.raceFill, spent / Math.max(1, r.limit));
    this.race.classList.toggle('late', s.started && left <= RACE_LATE_TICKS);
    this.race.classList.toggle('out', s.started && left <= 0);
    const won = s.result === 'won';
    setText(
      this.raceNote,
      !s.started ? t('bet.race.ready') : won ? t(spent <= r.brann ? 'bet.race.beatBrann' : 'bet.race.inTime', { t: clock(spent) }) : spent < r.brann ? t('bet.race.brannAt', { t: clock(r.brann) }) : t('bet.race.brannPast'),
    );
    this.race.classList.toggle('past', s.started && spent >= r.brann && !won);
  }

  /** Once per sim tick. */
  update(): void {
    const s = this.s();
    const d = s.dungeon;
    const L = s.map.dungeon;
    if (!d || !L) return;
    this.party.update(s);
    this.raceUpdate(s);
    if (s.rules?.neck) {
      setText(this.neckEl, neckLine(s));
      this.neckEl.classList.toggle('hot', !!s.neck?.warned);
      this.neckEl.classList.toggle('shut', !(s.neck?.open ?? true));
    }
    if (d.sima) {
      this.simaUpdate(s);
      this.marksAndFallen(s);
      return;
    }
    if (d.deep) {
      this.deepUpdate(s);
      this.marksAndFallen(s);
      return;
    }
    // Where we are.
    const rooms = L.rooms.length - 2;
    const next = d.cleared.findIndex((c, i) => !c && i > 0);
    const left = s.enemies.filter((e) => e.hp > 0 && e.kind !== 'warden' && e.kind !== 'drinker').length;
    const bossRoom = L.rooms.length - 1;
    if (s.drink?.burst) {
      // El pozo 2: the Drinker burst, and the water comes up behind.
      // La Orilla 4 (`up`): the way back from the bones, with the heart if they took it.
      const k = s.rules?.drinker?.up ? 'side.flight' : 'side.drinker';
      const heart = s.drink.heart;
      setText(this.whereTitle, t(s.drink.out ? `${k}.outTitle` : `${k}.runTitle`));
      setText(this.whereSub, s.drink.out ? '' : heart && heart.at !== 'carried' ? t('side.flight.heartDown') : heart ? t('side.flight.heartSub', { name: this.nameOf(s.heroes.find((h) => h.pid === heart.pid) ?? s.heroes[0]!) }) : t(`${k}.runSub`));
    } else if (d.room === bossRoom) {
      setText(this.whereTitle, t(s.rules?.drinker ? 'side.drinker.hall' : d.hallWins ? 'dungeon.exit' : 'dungeon.hall'));
      setText(this.whereSub, left ? t(left === 1 ? 'dungeon.bone' : 'dungeon.bones', { n: left }) : '');
    } else if (d.room > 0) {
      setText(this.whereTitle, t('dungeon.room', { n: d.room, total: rooms }));
      setText(this.whereSub, d.nextAt > 0 ? t('dungeon.nextWave') : t('dungeon.left', { n: left, w: d.wave + 1, waves: DUNGEON.wavesPerRoom }));
    } else {
      setText(this.whereTitle, !s.started ? t('dungeon.gathering') : next === bossRoom ? t(s.rules?.drinker ? 'side.drinker.toHall' : d.hallWins ? 'dungeon.toExit' : 'dungeon.toHall') : t('dungeon.toRoom', { n: next, total: rooms }));
      setText(this.whereSub, !s.started ? t('dungeon.gatherSub') : t('dungeon.walkOn'));
    }
    this.where.classList.toggle('fight', d.room > 0);
    // The Warden.
    const w = s.enemies.find((e) => e.id === d.boss && e.hp > 0);
    this.boss.classList.toggle('show', !!w);
    if (w) {
      const leech = w.kind === 'drinker';
      setText(this.bossName, t(leech ? 'enemy.drinker' : 'dungeon.warden'));
      setFill(this.bossFill, ((100 * w.hp) / w.maxHp) / 100);
      setText(this.bossPhase, leech ? t(`side.drinker.phase${w.phase}`) : t(`dungeon.phase${w.phase}`));
      this.boss.classList.toggle('roar', leech ? w.phase === 2 : d.roarUntil > s.tick);
      for (const tick of this.boss.querySelectorAll<HTMLElement>('.dg-tick')) tick.hidden = leech;
    }
    this.marksAndFallen(s);
  }

  /** Say what to do the first time each warning lands near me, and when a friend falls. */
  private marksAndFallen(s: BattleState): void {
    const d = s.dungeon;
    if (!d) return;
    const mine = s.heroes.find((x) => x.pid === this.me);
    if (mine && mine.downTicks === 0) {
      for (const m of d.marks) {
        if (this.warned.has(m.id)) continue;
        const dx = mine.x - m.x;
        const dy = mine.y - m.y;
        const inside = m.kind === 'ring' ? dx * dx + dy * dy > m.r * m.r : dx * dx + dy * dy <= m.r * m.r;
        if (!inside) continue;
        this.warned.add(m.id);
        const key = m.kind === 'slam' ? 'dungeon.dodgeSlam' : m.kind === 'nova' ? 'dungeon.dodgeNova' : 'dungeon.dodgeRing';
        this.toast(t(key), 1600);
      }
    }
    const fallen = s.heroes.find((x) => x.downTicks > 0 && x.pid !== this.me);
    if (fallen && !this.hinted.has(`down:${fallen.pid}`)) {
      this.hinted.add(`down:${fallen.pid}`);
      this.toast(t('dungeon.fallen', { name: this.nameOf(fallen) }), 3200);
    }
  }

  /**
   * The deep crypt: the floor and the room, the guardian's bar, and the goal panel — the puzzle's one-line
   * order with its progress, or, after a guardian, the stairs down and the way out with the chest so far.
   */
  private deepUpdate(s: BattleState): void {
    const d = s.dungeon;
    const L = s.map.dungeon;
    const dp = d?.deep;
    if (!d || !L || !dp) return;
    const floorName = t(`deep.floorName.${dp.floor}`);
    const here = d.room >= 0 ? L.rooms[d.room] : undefined;
    const left = s.enemies.filter((e) => e.hp > 0 && e.id !== d.boss).length;
    const pz = d.room >= 0 ? d.puzzles?.find((p) => p.room === d.room) : undefined;
    setText(this.whereTitle, t('deep.floor', { n: dp.floor + 1, total: dp.floors, name: floorName }));
    this.whereSub.textContent = !s.started
      ? t('dungeon.gatherSub')
      : here?.kind === 'boss'
        ? t(`deep.guardian.${dp.floor}`)
        : pz
          ? t(`puzzle.${pz.kind}.title`)
          : here
            ? t('dungeon.left', { n: left, w: d.wave + 1, waves: DUNGEON.wavesPerRoom })
            : dp.stairs
              ? t('deep.chooseShort')
              : t('dungeon.walkOn');
    this.where.classList.toggle('fight', d.room > 0);
    // The guardian.
    const g = s.enemies.find((e) => e.id === d.boss && e.hp > 0);
    this.boss.classList.toggle('show', !!g);
    this.boss.classList.toggle('deep', true);
    if (g) {
      setText(this.bossName, t(`deep.guardian.${dp.floor}`));
      setFill(this.bossFill, ((100 * g.hp) / g.maxHp) / 100);
      const shine = (g.shineUntil ?? 0) > s.tick;
      setText(this.bossPhase, g.kind === 'warden' ? t(`dungeon.phase${g.phase}`) : shine ? t('colossus.shineTag') : '');
      this.boss.classList.toggle('roar', d.roarUntil > s.tick || shine);
      for (const tick of this.boss.querySelectorAll<HTMLElement>('.dg-tick')) tick.hidden = g.kind !== 'warden';
    }
    // Moments worth a word.
    if (dp.floor !== this.seen.floor) {
      this.seen.floor = dp.floor;
      this.toast(t('deep.down', { n: dp.floor + 1, name: floorName }), 3200);
    }
    if (!!dp.stairs !== this.seen.stairs) {
      this.seen.stairs = !!dp.stairs;
      if (dp.stairs) {
        this.toast(t('deep.stairsOpen'), 3600);
        this.setFolded(small());
      }
    }
    for (const p of d.puzzles ?? []) {
      if (p.solved && !this.seen.solved.has(p.room)) {
        this.seen.solved.add(p.room);
        this.toast(t('puzzle.solved'), 2600);
      }
      if (p.fails > (this.seen.fails.get(p.room) ?? 0)) {
        this.seen.fails.set(p.room, p.fails);
        this.toast(t(`puzzle.${p.kind}.fail`), 2600);
      }
    }
    // The goal panel.
    const show = (title: string, text: string, bars: [number, number?] | null): void => {
      this.goal.classList.add('show');
      setText(this.goalTitle, title);
      setText(this.goalText, text);
      this.goalBars.hidden = !bars;
      if (bars) {
        setFill(this.goalBar, (Math.round(Math.min(1, Math.max(0, bars[0])) * 100)) / 100);
        setFill(this.goalBar2, (Math.round(Math.min(1, Math.max(0, bars[1] ?? 0)) * 100)) / 100);
        this.goalBar2.parentElement?.toggleAttribute('hidden', bars[1] === undefined);
      }
    };
    if (!this.goalBars.childElementCount) {
      this.goalBars.append(h('div.bar.dg-goal-bar', {}, this.goalBar), h('div.bar.dg-goal-bar.alt', {}, this.goalBar2));
    }
    this.goal.classList.toggle('stairs', !!dp.stairs && !(pz && !pz.solved));
    this.goal.classList.toggle('puzzle', !!pz && !pz.solved);
    if (pz && !pz.solved) {
      // A new puzzle: the whole order first; on a phone it folds to its title and bar after a while, so the room shows.
      if (pz.room !== this.seen.puzzle) {
        this.seen.puzzle = pz.room;
        this.setFolded(false);
        this.foldAt = small() ? s.tick + PUZZLE_FOLD_TICKS : 0;
      }
      if (this.foldAt && s.tick >= this.foldAt) {
        this.foldAt = 0;
        this.setFolded(true);
      }
      const [text, bars] = this.puzzleLine(s, pz);
      const press = pz.pressAt > s.tick ? Math.ceil((pz.pressAt - s.tick) / 12) : 0;
      show(t(`puzzle.${pz.kind}.title`), `${text}${press ? ` ${t('puzzle.press', { n: press })}` : ''}`, bars);
      return;
    }
    if (dp.stairs) {
      const now = deepChest(dp.cleared, false);
      const next = deepChest(dp.cleared + 1, false);
      // Two sides, each with what it brings: the stairs (more below, and the risk) and the way out (the chest now).
      show(t('deep.choose'), t('deep.chooseHint'), null);
      this.down.gain.innerHTML = `${ICONS.shard}<b>+${next.shards - now.shards}</b> ${t('deep.each')}`;
      setText(this.down.note, t('deep.downRisk', { half: DEEP_CRYPT.fallBp / 100 }));
      this.back.gain.innerHTML = `<b>${now.xp}</b> ${t('deep.xp')} · ${ICONS.shard}<b>${now.shards}</b> ${t('deep.each')}`;
      setText(this.back.note, t('deep.backSafe'));
      this.down.set(dp.stairsT / DEEP_CRYPT.chooseTicks);
      this.back.set(dp.exitT / DEEP_CRYPT.chooseTicks);
      return;
    }
    this.goal.classList.remove('show');
  }

  /**
   * The Chasm (docs/SIMA.md): the floor, its stratum and signs; the captain's or guardian's bar with its rule, and
   * what it is doing right now; the puzzle when there is one; and after the hall the stairs down or the way out,
   * each with what it brings.
   */
  private simaUpdate(s: BattleState): void {
    const d = s.dungeon;
    const L = s.map.dungeon;
    const sm = d?.sima;
    if (!d || !L || !sm) return;
    const stratum = t(`sima.stratum.${sm.stratum}`);
    const here = d.room >= 0 ? L.rooms[d.room] : undefined;
    const left = s.enemies.filter((e) => e.hp > 0 && e.id !== sm.boss).length;
    const pz = d.room >= 0 ? d.puzzles?.find((p) => p.room === d.room) : undefined;
    const guardian = STRATUM[sm.stratum].guardian;
    setText(this.whereTitle, t('sima.floor', { n: sm.floor, name: stratum }));
    this.whereSub.textContent = !s.started
      ? t('dungeon.gatherSub')
      : here?.kind === 'boss'
        ? sm.threshold
          ? t(`sima.guardian.${guardian}`)
          : t('sima.captain')
        : pz && !pz.solved
          ? t(`puzzle.${pz.kind}.title`)
          : here
            ? t('dungeon.left', {
                n: left,
                w: d.wave + 1,
                waves: DUNGEON.wavesPerRoom,
              })
            : sm.stairs
              ? t('deep.chooseShort')
              : sm.signs.length
                ? sm.signs
                    .map((g) =>
                      t(`sima.sign.${g}`, {
                        type: sm.ward ? t(`dtype.${sm.ward}`) : '',
                      }),
                    )
                    .join(' · ')
                : t('dungeon.walkOn');
    this.where.classList.toggle('fight', d.room > 0);
    this.where.style.setProperty('--stratum', STRATUM[sm.stratum].color);
    // A new floor: its name, its band and its signs, once.
    if (sm.rev !== this.simaSeen.rev) {
      this.simaSeen.rev = sm.rev;
      this.seen.solved.clear();
      this.seen.fails.clear();
      this.seen.stairs = false;
      this.seen.puzzle = -1;
      const signs = sm.signs.map((g) => t(`sima.sign.${g}`, { type: sm.ward ? t(`dtype.${sm.ward}`) : '' })).join(' · ');
      this.toast(`${t('sima.arrive', { n: sm.floor, name: stratum })}${signs ? ` — ${signs}` : ''}`, 4200);
      const band = `band:${sm.band}`;
      if (sm.band !== 'shadow' && !this.simaSeen.said.has(band)) {
        this.simaSeen.said.add(band);
        this.toast(t(`sima.band.${sm.band}`), 4200);
      }
    }
    // The first time each hazard is close to me: what it does, in a line.
    const mine = s.heroes.find((x) => x.pid === this.me);
    if (mine && mine.downTicks === 0) {
      for (const z of s.zones) {
        if (z.kind !== 'sima' || !z.haz || this.simaSeen.said.has(`haz:${z.haz}`)) continue;
        const r = z.radius + 1500;
        if ((mine.x - z.x) ** 2 + (mine.y - z.y) ** 2 > r * r) continue;
        this.simaSeen.said.add(`haz:${z.haz}`);
        this.toast(t(`sima.haz.${z.haz}`), 3600);
      }
      if ((mine.held ?? 0) > 0 && !this.simaSeen.said.has('held')) {
        this.simaSeen.said.add('held');
        this.toast(t('sima.held'), 2600);
      }
    }
    // The captain or the guardian.
    const g = s.enemies.find((e) => e.id === sm.boss && e.hp > 0);
    this.boss.classList.toggle('show', !!g);
    this.boss.classList.toggle('deep', true);
    for (const tick of this.boss.querySelectorAll<HTMLElement>('.dg-tick')) tick.hidden = g?.kind !== 'warden';
    const act = sm.act && sm.act.by === sm.boss ? sm.act : null;
    if (g) {
      setText(this.bossName, sm.threshold ? t(`sima.guardian.${guardian}`) : t('sima.captainOf', { name: t(`enemy.${g.kind}`) }));
      setFill(this.bossFill, (100 * g.hp) / g.maxHp / 100);
      const shine = (g.shineUntil ?? 0) > s.tick;
      setText(this.bossPhase, act ? t(`sima.act.${act.kind}`) : g.kind === 'warden' ? t(`dungeon.phase${g.phase}`) : shine ? t('colossus.shineTag') : '');
      this.boss.classList.toggle('roar', !!act || d.roarUntil > s.tick || shine);
      // Each guardian says who it is and its one rule the first time the group meets it (then only its name).
      const key = `somnhold.sima.met.${guardian}`;
      if (sm.threshold && !this.simaSeen.said.has(`g:${guardian}`)) {
        this.simaSeen.said.add(`g:${guardian}`);
        let met = false;
        try {
          met = localStorage.getItem(key) === '1';
          localStorage.setItem(key, '1');
        } catch {
          /* private window: say it every time */
        }
        this.toast(met ? t(`sima.guardian.${guardian}`) : `${t(`sima.guardian.${guardian}`)}: ${t(`sima.rule.${guardian}`)}`, met ? 2600 : 6000);
      }
      if (act && act.kind !== this.simaSeen.act) this.toast(t(`sima.actHint.${act.kind}`), 2600);
      this.simaSeen.act = act?.kind ?? '';
    }
    // The goal panel.
    const show = (title: string, text: string, bars: [number, number?] | null): void => {
      this.goal.classList.add('show');
      setText(this.goalTitle, title);
      setText(this.goalText, text);
      this.goalBars.hidden = !bars;
      if (bars) {
        setFill(this.goalBar, Math.round(Math.min(1, Math.max(0, bars[0])) * 100) / 100);
        setFill(this.goalBar2, Math.round(Math.min(1, Math.max(0, bars[1] ?? 0)) * 100) / 100);
        this.goalBar2.parentElement?.toggleAttribute('hidden', bars[1] === undefined);
      }
    };
    if (!this.goalBars.childElementCount) this.goalBars.append(h('div.bar.dg-goal-bar', {}, this.goalBar), h('div.bar.dg-goal-bar.alt', {}, this.goalBar2));
    this.goal.classList.toggle('stairs', !!sm.stairs);
    this.goal.classList.toggle('puzzle', !!pz && !pz.solved);
    // The archivist reading: how much of it is left to cut, under his own bar (the goal panel sits in the same place).
    const reading = act?.kind === 'read' && act.bar !== undefined && !!act.barMax;
    this.read.classList.toggle('show', reading);
    if (reading) setFill(this.readFill, (Math.round(((act?.bar ?? 0) / (act?.barMax ?? 1)) * 100)) / 100);
    if (pz && !pz.solved) {
      if (pz.room !== this.seen.puzzle) {
        this.seen.puzzle = pz.room;
        this.setFolded(false);
        this.foldAt = small() ? s.tick + PUZZLE_FOLD_TICKS : 0;
      }
      if (this.foldAt && s.tick >= this.foldAt) {
        this.foldAt = 0;
        this.setFolded(true);
      }
      const [text, bars] = this.puzzleLine(s, pz);
      show(t(`puzzle.${pz.kind}.title`), text, bars);
      return;
    }
    for (const p of d.puzzles ?? []) {
      if (p.solved && !this.seen.solved.has(p.room)) {
        this.seen.solved.add(p.room);
        this.toast(t('puzzle.solved'), 2600);
      }
      if (p.fails > (this.seen.fails.get(p.room) ?? 0)) {
        this.seen.fails.set(p.room, p.fails);
        this.toast(t(`puzzle.${p.kind}.fail`), 2600);
      }
    }
    if (sm.stairs) {
      if (!this.seen.stairs) {
        this.seen.stairs = true;
        this.toast(t('deep.stairsOpen'), 3600);
        this.setFolded(small());
      }
      const now = simaChest(sm.start, sm.done, false);
      const next = simaChest(sm.start, sm.done + 1, false);
      show(t('deep.choose'), t('sima.chooseHint', { n: sm.floor + 1 }), null);
      this.down.gain.innerHTML = `<b>+${next.xp - now.xp}</b> ${t('deep.xp')} · ${ICONS.chest}<b>+${next.dust - now.dust}</b>`;
      setText(this.down.note, t('deep.downRisk', { half: SIMA_CHEST.fallBp / 100 }));
      this.back.gain.innerHTML = `<b>${now.xp}</b> ${t('deep.xp')} · ${ICONS.chest}<b>${now.dust}</b>`;
      setText(this.back.note, sm.thresholds.length ? t('sima.backPieces', { n: sm.thresholds.length }) : t('deep.backSafe'));
      this.down.set(sm.stairsT / SIMA.chooseTicks);
      this.back.set(sm.exitT / SIMA.chooseTicks);
      return;
    }
    this.goal.classList.remove('show');
  }

  private setFolded(on: boolean): void {
    if (!on) this.foldAt = 0;
    this.goal.classList.toggle('folded', on);
    setText(this.fold, on ? '+' : '–');
  }

  /** Walks my hero to a spot (the stairs, the way out, a door), as a tap on the floor there would. */
  private goTo(at: Hex | null | undefined): void {
    if (!at) return;
    const c = hexToWorld(at.q, at.r);
    this.send({ t: 'move', x: c.x, y: c.y });
  }

  /** The puzzle's order for right now, and its progress (0..1). */
  private puzzleLine(s: BattleState, p: DungeonPuzzle): [string, [number, number?] | null] {
    if (p.kind === 'braziers') {
      const lit = p.lit.filter((x) => x > s.tick).length;
      const soonest = Math.min(...p.lit.filter((x) => x > s.tick).map((x) => x - s.tick), DEEP_CRYPT.braziers.burnTicks);
      return [t('puzzle.braziers.do', { lit, n: p.spots.length }), [lit / p.spots.length, lit ? soonest / DEEP_CRYPT.braziers.burnTicks : 0]];
    }
    if (p.kind === 'levers') {
      const a = p.step * 2;
      const on = (p.on.includes(a) ? 1 : 0) + (p.on.includes(a + 1) ? 1 : 0);
      return [t('puzzle.levers.do', { round: p.step + 1, rounds: p.spots.length / 2, on }), [p.hold / p.need]];
    }
    if (p.kind === 'runes') {
      if (s.tick < p.showUntil) return [t('puzzle.runes.watch'), null];
      return [t('puzzle.runes.do', { k: p.step, n: p.spots.length }), [p.step / p.spots.length]];
    }
    if (s.tick < p.showUntil) return [t('puzzle.glass.watch'), null];
    return [t('puzzle.glass.do'), [p.step / Math.max(1, p.spots.length - 1)]];
  }

  private nameOf(hero: Hero): string {
    return this.party.nameOf(hero);
  }

  /**
   * Between fights, where the party goes next: the door into the next room (sealed or not), or after a
   * deep crypt guardian the stairs down and the way out. Nothing while a room is being fought.
   */
  private nextWays(s: BattleState): Way[] {
    const d = s.dungeon;
    const L = s.map.dungeon;
    if (!d || !L || !s.started || s.result !== 'running' || (d.room >= 0 && !s.drink?.burst)) return [];
    // El pozo 2: after the burst, the way out is back up to the first room.
    const entry = s.rules?.drinker?.up ? L.rooms[L.rooms.length - 1] : L.rooms[0];
    const hh = s.drink?.heart;
    if (hh?.at === 'ground') return [{ at: worldToHex(hh.x, hh.y), label: t('side.flight.heartWay') }];
    if (s.drink?.burst && entry) return [{ at: { q: entry.q, r: entry.r }, label: t(s.rules?.drinker?.up ? 'side.flight.wayOut' : 'side.drinker.wayOut') }];
    if (d.deep?.stairs && d.deep.exit) return [{ at: d.deep.stairs, label: t('deep.downName') }, { at: d.deep.exit, label: t('deep.backName') }];
    if (d.sima?.stairs && d.sima.exit) return [{ at: d.sima.stairs, label: t('deep.downName') }, { at: d.sima.exit, label: t('deep.backName') }];
    // Door i leads into room i + 1: the first one past a cleared room into one still standing (a lower floor's entrance comes by the stairs).
    const i = L.doors.findIndex((_, k) => d.cleared[k] && !d.cleared[k + 1] && L.rooms[k + 1]?.kind !== 'entry');
    const door = L.doors[i];
    if (!door) return [];
    return [{ at: door, label: L.rooms[i + 1]?.kind !== 'boss' ? t('dungeon.wayNext') : s.rules?.drinker ? t('side.drinker.hall') : d.hallWins ? t('dungeon.wayExit') : t('dungeon.wayHall') }];
  }

  /** The ways on screen: an arrow over each one in view, or one held at the edge pointing to it. */
  private placeWays(positions: Map<number, THREE.Vector3>, camera: THREE.Camera): void {
    const s = this.s();
    const mine = s.heroes.find((x) => x.pid === this.me);
    const mp = mine ? positions.get(mine.id) : undefined;
    const ways = mine && mine.downTicks === 0 ? this.nextWays(s) : [];
    const W = viewW();
    const H = viewH();
    const v = new THREE.Vector3();
    ways.forEach((w, k) => {
      let el = this.wayEls[k];
      if (!el) {
        // A tap on the arrow walks you there.
        const i = k;
        el = h('button.dg-way', { type: 'button', onclick: () => this.goTo(this.nextWays(this.s())[i]?.at) }, h('span.dg-way-arrow', { html: CHEVRON }), h('span.dg-way-txt'));
        this.ways.append(el);
        this.wayEls[k] = el;
      }
      const c = hexToWorld(w.at.q, w.at.r);
      const p = simToWorld(c.x, c.y);
      // Already there (two hexes or less): no need to point.
      const near = !!mp && Math.hypot(mp.x - p.x, mp.z - p.z) < WORLD_PER_HEX * 2;
      setHidden(el, near);
      if (near) return;
      setText(el.lastElementChild as HTMLElement, w.label);
      v.copy(p).setY(1.4).project(camera);
      const behind = v.z > 1;
      let x = (v.x * 0.5 + 0.5) * W;
      let y = (-v.y * 0.5 + 0.5) * H;
      if (behind) {
        x = W - x;
        y = H - y;
      }
      // The frame it may sit in: clear of the edges, and below the chips along the top (where we are, the party).
      const m = 46;
      const top = 120;
      const inView = !behind && x > m && x < W - m && y > top && y < H - m;
      el.classList.toggle('edge', !inView);
      let turn = 0;
      if (!inView) {
        // Held on that frame, along the line from its middle toward the way, and turned to face it.
        const cx = W / 2;
        const cy = (top + H - m) / 2;
        const dx = x - cx;
        const dy = y - cy;
        const k2 = Math.min((W / 2 - m) / Math.max(1, Math.abs(dx)), ((H - m - top) / 2) / Math.max(1, Math.abs(dy)));
        x = cx + dx * k2;
        y = cy + dy * k2;
        turn = Math.atan2(dy, dx) - Math.PI / 2;
      }
      setStyle(el, 'transform', `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) translate(-50%, -50%)`);
      setStyle(el.firstElementChild as HTMLElement, 'transform', `rotate(${turn.toFixed(3)}rad)`);
    });
    for (let k = ways.length; k < this.wayEls.length; k++) setHidden(this.wayEls[k] as HTMLElement, true);
  }

  /** Every animation frame: labels over fallen heroes and the way on, projected on screen. */
  place(positions: Map<number, THREE.Vector3>, camera: THREE.Camera): void {
    this.placeWays(positions, camera);
    const s = this.s();
    const seen = new Set<string>();
    const v = new THREE.Vector3();
    for (const hero of s.heroes) {
      if (hero.downTicks === 0) continue;
      const p = positions.get(hero.id);
      if (!p) continue;
      seen.add(hero.pid);
      let tag = this.tagEls.get(hero.pid);
      if (!tag) {
        tag = h('div.dg-tag', {}, h('span.dg-ring'), h('span.dg-txt'));
        this.tags.append(tag);
        this.tagEls.set(hero.pid, tag);
      }
      const k = Math.min(1, (hero.revive ?? 0) / DUNGEON.reviveTicks);
      setStyle(tag.firstElementChild as HTMLElement, '--p', `${Math.round(k * 100)}%`);
      setText(tag.lastElementChild as HTMLElement, k > 0 ? t('dungeon.reviving') : hero.pid === this.me ? t('dungeon.waitHelp') : t('dungeon.reviveHint', { name: this.nameOf(hero) }));
      v.copy(p).setY(p.y + 1.2).project(camera);
      setStyle(tag, 'transform', `translate(${((v.x * 0.5 + 0.5) * viewW()).toFixed(1)}px, ${((-v.y * 0.5 + 0.5) * viewH()).toFixed(1)}px) translate(-50%, -100%)`);
    }
    for (const [pid, tag] of this.tagEls) {
      if (seen.has(pid)) continue;
      tag.remove();
      this.tagEls.delete(pid);
    }
  }
}
